'use client'

/**
 * The navy pill's shared parts: its focus ring, its icon controls, its live
 * dot and its one-width-per-state name slot.
 *
 * Extracted from `recording-bar.tsx` when that file reached
 * `check:file-size-ratchet`'s 1,000-line cap (#13840). The guard's
 * prescription is to split rather than to raise a baseline, and these four
 * are the pieces with no knowledge of which STATE the bar is in: they are
 * drawn the same way by the before, recording and collapsed branches.
 */

import Link from 'next/link'
import type { HTMLAttributes, ReactNode, Ref } from 'react'

import { NAV_ENTRY_ICONS } from '@/lib/ui-icons'
import { RECORDING_BAR_PULSE_CLASS } from '@/lib/notetaker/recording-bar'
import { notePath } from '@/lib/notes/note-paths'
import { cn } from '@/lib/utils'

import {
  RECORDING_BAR_NAME_SLOT_MIN_WIDTH,
  RECORDING_BAR_TEXT_MAX_WIDTH,
} from './recording-bar-width'

const NoteIcon = NAV_ENTRY_ICONS.notes

/** The focus ring every control on the navy pill wears. */
export const FOCUS_RING_CLASS = cn(
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
  'focus-visible:outline-recording-bar-ink',
)

/** The paper pill's ring (#14652): white ink is invisible on paper. */
const PAPER_FOCUS_RING_CLASS = cn(
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
  'focus-visible:outline-stroke-selected',
)

/**
 * A link out of the bar — or, ON THE PAGE IT LEADS TO, the same text marked
 * `aria-current="page"` and no link (#14402). The bar floats over the note it
 * records into, and a click there would reload the page being recorded into.
 */
export function BarLink({
  href,
  currentPath,
  children,
  ...rest
}: HTMLAttributes<HTMLElement> & {
  href: string
  currentPath: string | null
  'data-testid'?: string
}) {
  if (href === currentPath) {
    // The caller's hover wash goes with the link: on text that goes nowhere
    // it reads as a broken link.
    return (
      <span
        aria-current="page"
        {...rest}
        className={cn(rest.className, 'hover:bg-transparent')}
      >
        {children}
      </span>
    )
  }
  return (
    <Link href={href} {...rest}>
      {children}
    </Link>
  )
}

/**
 * The 44x44 hit area on a coarse pointer. The circle inside it is the
 * canvas's smaller one.
 */
const ICON_CONTROL_CLASS = cn(
  'group inline-flex pointer-coarse:min-h-11 pointer-coarse:min-w-11 items-center justify-center rounded-full',
  'disabled:opacity-60',
)

/**
 * One transport control: a 44px target wrapping the circle the canvas draws.
 *
 * `tone` is the canvas's three treatments. `solid` (Stop) is the only filled
 * one — stopping is the consequential press, and a white disc on navy is the
 * loudest thing the bar can say without colour. `outline` (Pause) is hairline
 * at rest. `quiet` (the chevrons) has no chrome at all until hovered, because
 * collapsing and expanding are the bar's own view state rather than anything
 * that touches the recording.
 */
export function IconControl({
  ref,
  testId,
  label,
  tone,
  paper = false,
  disabled,
  onClick,
  children,
}: {
  ref?: Ref<HTMLButtonElement>
  testId: string
  label: string
  tone: 'solid' | 'outline' | 'quiet'
  /**
   * The control sits on the paper pill (#14652). Every tone gets a focus ring
   * that shows on paper; the QUIET tone also takes page ink. The quiet
   * controls drawn on paper are the mic check's cancel and the error's
   * Dismiss.
   */
  paper?: boolean
  disabled?: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      ref={ref}
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={cn(
        ICON_CONTROL_CLASS,
        paper ? PAPER_FOCUS_RING_CLASS : FOCUS_RING_CLASS,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'flex items-center justify-center rounded-full transition-colors',
          'motion-reduce:transition-none',
          tone === 'quiet'
            ? paper
              ? cn(
                  'h-7 w-7 text-text-secondary',
                  'group-hover:bg-surface-panel',
                  'group-hover:text-text-primary',
                )
              : cn(
                  'h-7 w-7 text-recording-bar-ink/40',
                  'group-hover:bg-recording-bar-ink/15',
                  'group-hover:text-recording-bar-ink',
                )
            : 'h-9 w-9',
          tone === 'outline' &&
            cn(
              'border border-recording-bar-ink/20 text-recording-bar-ink',
              'group-hover:border-recording-bar-ink/30',
              'group-hover:bg-recording-bar-ink/15',
            ),
          tone === 'solid' &&
            cn(
              'bg-recording-bar-ink text-recording-bar-surface',
              'group-hover:bg-recording-bar-ink/90',
            ),
        )}
      >
        {children}
      </span>
    </button>
  )
}

/**
 * The live dot: a 7px core inside a soft halo.
 *
 * The halo is what makes a single small dot read as "on air" rather than as a
 * bullet — and it is a sibling span rather than a ring utility so the pulse
 * animates the core alone, leaving a steady edge behind it.
 */
export function LiveDot({
  reducedMotion,
  size,
  tone = 'live',
  testId = 'recording-bar-dot',
  paper = false,
}: {
  reducedMotion: boolean
  size: 'sm' | 'md'
  /** A second dot on screen needs its own testid (#15223). */
  testId?: string
  /**
   * `saved` (#14384): the microphone has closed, so the dot turns grey — an
   * opacity step over the bar's ink — and stops pulsing.
   */
  tone?: 'live' | 'saved'
  /**
   * Drawn on a light surface (the live draft panel's header, #15223) rather
   * than on the bar: the bar's ink is near-white, so a `saved` dot in it
   * would vanish there. Mirrors `LevelMeter`'s `paper`.
   */
  paper?: boolean
}) {
  const live = tone === 'live'
  const savedInk = paper ? 'bg-text-secondary' : 'bg-recording-bar-ink/50'
  const savedHalo = paper ? 'bg-text-secondary/20' : 'bg-recording-bar-ink/15'
  return (
    <span
      className={cn(
        'relative flex shrink-0 items-center justify-center',
        size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'absolute inset-0 rounded-full',
          live ? 'bg-recording-bar-live/30' : savedHalo,
        )}
      />
      <span
        data-testid={testId}
        data-tone={tone}
        aria-hidden="true"
        className={cn(
          'relative h-2 w-2 rounded-full',
          live ? 'bg-recording-bar-live' : savedInk,
          // Reduced motion QUIETS the pulse and keeps the dot: it is the only
          // always-visible indicator that a microphone is open, so removing it
          // would delete the state rather than calm it. globals.css carries the
          // same suppression for the OS setting changing mid-animation.
          live && !reducedMotion && RECORDING_BAR_PULSE_CLASS,
        )}
      />
    </span>
  )
}

/**
 * The name slot's sizer: the name, drawn invisibly in the slot's one grid
 * cell. Height 0, so it widens the cell without adding a line.
 */
export const NAME_SLOT_SIZER_CLASS = cn(
  'after:invisible after:col-start-1 after:row-start-1 after:h-0',
  'after:overflow-hidden after:content-[attr(data-name)]',
)

/**
 * The recording state's name slot: ONE box per bar state, whatever it holds.
 *
 * `swapped` changes the CONTENT and never the box. The box is a one-cell grid
 * whose width comes from an invisible SIZER — an `::after` drawing the name
 * from `data-name`, so the name is not in the DOM twice (#14933). It is as
 * wide as the name, never narrower than room for "Open
 * note", never wider than the bar's cap — and once the bar is at its cap, flex
 * shrinks it and the name ellipsises. The sizer holds the name in all three
 * cases, so hover and focus never recompute the width (A3/A4). It used to be a
 * fixed 200px, which cut ordinary meeting names off.
 *
 * It links the capture's NOTE (#14402), never the meeting room: the note is
 * the artifact. No note id is plain text; the note's own page is `BarLink`'s
 * current text.
 */
export function NameSlot({
  noteId,
  meetingName,
  currentPath,
  swapped,
  onEnter,
  onLeave,
}: {
  noteId: string | null
  meetingName: string
  currentPath: string | null
  swapped: boolean
  onEnter: () => void
  onLeave: () => void
}) {
  const href = noteId ? notePath(noteId) : null
  const shared = cn(
    'col-start-1 row-start-1 block w-full min-w-0',
    'overflow-hidden text-ellipsis whitespace-nowrap rounded-full',
    'text-left text-sm text-recording-bar-ink/60 transition-colors',
    'hover:text-recording-bar-ink',
    FOCUS_RING_CLASS,
    'motion-reduce:transition-none',
  )

  return (
    <div
      data-testid="recording-bar-name-slot"
      // Sized by the sizer below and these two bounds, none of which reads
      // `swapped`: the slot is this wide holding a short name, a long name
      // ellipsised, or the swapped-in button (A3), demonstrated on A4.
      style={{
        minWidth: RECORDING_BAR_NAME_SLOT_MIN_WIDTH,
        maxWidth: RECORDING_BAR_TEXT_MAX_WIDTH,
      }}
      data-name={meetingName}
      className={cn(
        'grid grid-cols-[minmax(0,1fr)] overflow-hidden whitespace-nowrap',
        'text-sm',
        NAME_SLOT_SIZER_CLASS,
      )}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onEnter}
      onBlur={onLeave}
    >
      {href === null ? (
        <span className={shared}>{meetingName}</span>
      ) : href !== currentPath ? (
        // One element either way: swapping in a second Link would unmount the
        // one Tab just focused, and keyboard focus would fall to <body>.
        <Link
          href={href}
          data-testid={swapped ? 'recording-bar-open-note' : undefined}
          className={cn(
            shared,
            swapped && [
              'inline-flex items-center justify-center gap-1.5 px-2 py-1',
              'bg-recording-bar-ink/15 text-xs font-semibold text-recording-bar-ink',
            ],
          )}
        >
          {swapped ? (
            <>
              <NoteIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              Open note
            </>
          ) : (
            meetingName
          )}
        </Link>
      ) : (
        <BarLink href={href} currentPath={currentPath} className={shared}>
          {meetingName}
        </BarLink>
      )}
    </div>
  )
}
